import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

const RaisPanel = lazy(() => import('./RaisPage').then(({ RaisPanel }) => ({ default: RaisPanel })))

const RaisDrawerContext = createContext<{ open: boolean; toggle: () => void } | null>(null)

/** RAIS opens in a side panel; the panel header links to the full page. */
export function useRaisDrawer() {
  return useContext(RaisDrawerContext)
}

export function RaisDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  // Mount on first open, then keep the conversation alive while the panel is closed or the user browses.
  const [mounted, setMounted] = useState(false)
  const panel = useRef<HTMLElement>(null)
  useEffect(() => { if (open) panel.current?.querySelector('textarea')?.focus({ preventScroll: true }) }, [open])
  const toggle = () => { setMounted(true); setOpen((current) => !current) }
  const keyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !event.defaultPrevented) setOpen(false) }
  return <RaisDrawerContext.Provider value={{ open, toggle }}>
    {children}
    {open && <button type="button" aria-label="Close RAIS by clicking outside" onClick={() => setOpen(false)}
      className="no-print fixed inset-0 z-40 cursor-default bg-transparent" />}
    {mounted && <aside ref={panel} aria-label="RAIS" hidden={!open} inert={!open} onKeyDown={keyDown}
      className="no-print fixed inset-y-0 right-0 z-50 w-full border-l border-navy-100 bg-white shadow-2xl sm:w-[520px]">
      <Suspense fallback={<div className="h-full bg-white" />}><RaisPanel onClose={() => setOpen(false)} /></Suspense>
    </aside>}
  </RaisDrawerContext.Provider>
}
